import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import type { AccountDevice, AccountDeviceEvent, AccountDeviceLog } from './account-types';
import './device-diagnostics.css';

/**
 * Under one device on the customer page (docs/engineering/folder-monitor.md, section 6; migration
 * 0147): what its folders sent, its last 20 events and the logs it sent to Ademero support, folded away
 * until support opens it. `logHref` is the download address for a staff role that may download
 * (support, engineering); without it the uploads are listed, not linked.
 */
export function DeviceDiagnostics({
  d,
  logHref,
}: {
  d: AccountDevice;
  logHref?: ((logId: string) => string) | undefined;
}): ReactNode {
  const f = useFormat();
  const events = d.events ?? [];
  const logs = d.logs ?? [];
  const errors = events.filter((e) => e.level === 'error').length;
  const counters = countersWord(d, f);
  if (events.length === 0 && logs.length === 0 && !counters) return null;
  return (
    <details className="ad-device-diag">
      <summary>
        {summaryWord(events.length, logs.length)}
        {errors ? (
          <span className="ad-tone-danger">
            {' '}
            · {errors} error{errors === 1 ? '' : 's'}
          </span>
        ) : null}
      </summary>
      {counters ? <p className="ad-small">{counters}</p> : null}
      <EventList name={d.name} events={events} />
      <LogList name={d.name} logs={logs} logHref={logHref} />
    </details>
  );
}

function EventList({ name, events }: { name: string; events: AccountDeviceEvent[] }): ReactNode {
  const f = useFormat();
  if (events.length === 0) return null;
  return (
    <ul className="ad-device-events" aria-label={`${name} events`}>
      {events.map((e, i) => (
        <EventRow key={`${e.at}-${i}`} e={e} when={f.dateTime(e.at)} />
      ))}
    </ul>
  );
}

function LogList(x: {
  name: string;
  logs: AccountDeviceLog[];
  logHref: ((logId: string) => string) | undefined;
}): ReactNode {
  const f = useFormat();
  if (x.logs.length === 0) return null;
  return (
    <ul className="ad-device-logs" aria-label={`${x.name} logs sent to support`}>
      {x.logs.map((l) => (
        <li key={l.id}>
          <time dateTime={l.receivedAt}>{f.dateTime(l.receivedAt)}</time>
          {x.logHref ? (
            <a href={x.logHref(l.id)} download>
              Logs, {sizeWord(l.sizeBytes)}
            </a>
          ) : (
            <span>Logs, {sizeWord(l.sizeBytes)}</span>
          )}
          {l.note ? <span className="ad-muted">“{l.note}”</span> : null}
        </li>
      ))}
    </ul>
  );
}

function EventRow({ e, when }: { e: AccountDeviceEvent; when: string }): ReactNode {
  const tone =
    e.level === 'error' ? 'ad-tone-danger' : e.level === 'warning' ? 'ad-tone-warning' : 'ad-muted';
  return (
    <li>
      <time dateTime={e.at}>{when}</time>
      <span>
        <strong className={tone}>{e.event.replace(/_/g, ' ')}</strong>
        {e.folder ? <span className="ad-muted"> · {e.folder}</span> : null}
        {e.message ? <span className="ad-device-msg">{e.message}</span> : null}
      </span>
    </li>
  );
}

const summaryWord = (events: number, logs: number) =>
  [
    events ? `${events} recent event${events === 1 ? '' : 's'}` : 'No recent events',
    logs ? `${logs} log upload${logs === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** "1.2 MB", "340 KB": how big a log upload or a day's sending was. */
export function sizeWord(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return bytes === 0 ? '0 KB' : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Its folders' counters in one line, or null when the device never sent them (before Paige Scan 1.6). */
function countersWord(d: AccountDevice, f: ReturnType<typeof useFormat>): string | null {
  const parts = [
    d.bytesToday != null ? `${sizeWord(d.bytesToday)} sent today` : null,
    d.retriesToday
      ? `${d.retriesToday} upload retr${d.retriesToday === 1 ? 'y' : 'ies'} today`
      : null,
    d.oldestWaitingAt ? `oldest waiting file arrived ${f.ago(d.oldestWaitingAt)}` : null,
    d.sentTotal != null ? `${f.int(d.sentTotal)} files sent in all` : null,
  ].filter((p): p is string => p !== null);
  return parts.length ? parts.join(' · ') : null;
}
